/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid, Tooltip } from '@mairistem/composants';
import { NaissanceForm } from '@mairistem/adresses';
import { EtatCivil, TypeComponent } from '@mairistem/administres-utils';

import { Section, SectionHeader } from '@/components/Common';
import { COLORS } from '@/constants';

interface InformationsNaissanceProps {
  numeroPartenaire: number;
  partenaire: any;
  errors: Record<string, string>;
  /** Date de naissance, sexe et nationalité obligatoires (défaut : true) */
  naissanceObligatoire?: boolean;
  onChange;
}

const InformationsNaissance = (props: InformationsNaissanceProps) => {
  const {
    numeroPartenaire,
    partenaire,
    errors,
    naissanceObligatoire = true,
    onChange,
  } = props;

  const testIdPrefix = `assistant-partenaire-${numeroPartenaire}-naissance`;

  const handleAdresseChange = React.useCallback(
    (adresse) => {
      onChange(null, {
        name: 'adresseNaissance',
        value: {
          ...partenaire?.adresseNaissance,
          ...adresse,
          ...(!_.isNil(adresse.commune)
            ? { libelleCommune: adresse.commune }
            : {}),
          ...(!_.isNil(adresse.codePostal)
            ? { libelleCodePostal: adresse.codePostal }
            : {}),
          ...(!_.isNil(adresse.pays) ? { libellePays: adresse.pays } : {}),
          ...(!_.isNil(adresse.departement)
            ? { libelleDepartement: adresse.departement }
            : {}),
        },
      });
    },
    [onChange, partenaire],
  );

  const handleNaissanceFieldChange = React.useCallback(
    (field: string, value: string | boolean) => {
      onChange(null, {
        name: field,
        value,
      });
    },
    [onChange],
  );

  return (
    <Section data-testid={testIdPrefix} title="INFORMATIONS DE NAISSANCE">
      {/* @ts-ignore */}
      <Form style={{ display: 'flex' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: '1em',
          }}
        >
          <EtatCivil
            title="État civil"
            individu={partenaire}
            errors={errors}
            onChange={onChange}
            typeComponent={TypeComponent.PACS}
            naissanceObligatoire={naissanceObligatoire}
            // Un partenaire peut ne pas avoir de prénom : confirmation demandée
            // au passage à l'étape suivante
            prenomObligatoire={false}
          />
        </div>
        <div
          style={{
            border: `1px solid ${COLORS.DividerColor}`,
            margin: '0px 2rem',
            width: 1,
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: '1em',
          }}
        >
          <SectionHeader title="Lieu de naissance" />
          <div style={{ paddingLeft: '1em', paddingTop: '1em' }}>
            <NaissanceForm
              adresse={{
                codePostal: partenaire?.adresseNaissance?.libelleCodePostal,
                commune: partenaire?.adresseNaissance?.libelleCommune,
                pays: partenaire?.adresseNaissance?.libellePays,
                departement: partenaire?.adresseNaissance?.libelleDepartement,
                codeDepartement: partenaire?.adresseNaissance?.codeDepartement,
              }}
              error={!_.isNil(errors?.adresseNaissance)}
              onAdresseChange={handleAdresseChange}
            />
            <Grid>
              <Grid.Row verticalAlign="middle" stretched>
                <Grid.Column width={6}>
                  <label>&nbsp;</label>
                  <Form.Checkbox
                    label="Commune déléguée"
                    data-testid={`${testIdPrefix}-commune-delegue`}
                    checked={
                      partenaire?.adresseNaissance?.naissanceCommuneDelegue
                    }
                    onChange={(_e, data) => {
                      handleNaissanceFieldChange(
                        'adresseNaissance.naissanceCommuneDelegue',
                        data?.checked,
                      );

                      if (!data?.checked) {
                        handleNaissanceFieldChange(
                          'adresseNaissance.naissanceLibelleCommuneDelegue',
                          '',
                        );
                      }
                    }}
                  />
                </Grid.Column>
                <Grid.Column width={10} style={{ display: 'flex' }}>
                  <Tooltip
                    trigger={
                      <div style={{ flex: 1 }}>
                        <Form.Input
                          label="&nbsp;"
                          fluid
                          input={{
                            'data-testid': `${testIdPrefix}-libelle-commune-delegue`,
                          }}
                          value={
                            partenaire?.adresseNaissance
                              ?.naissanceLibelleCommuneDelegue ?? ''
                          }
                          disabled={
                            !partenaire?.adresseNaissance
                              ?.naissanceCommuneDelegue
                          }
                          error={!!errors?.naissanceLibelleCommuneDelegue}
                          onChange={(_e: any, data: any) =>
                            handleNaissanceFieldChange(
                              'adresseNaissance.naissanceLibelleCommuneDelegue',
                              data?.value ?? '',
                            )
                          }
                        />
                      </div>
                    }
                    type={errors?.naissanceLibelleCommuneDelegue ? 'error' : ''}
                    message={errors?.naissanceLibelleCommuneDelegue}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
        </div>
      </Form>
    </Section>
  );
};

export default InformationsNaissance;
